import { AlertTriangle, Settings, User, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SHOW_WORKER_COLOR_DOT } from "@/lib/constants/feature-flags";
import type { SelectedService } from "../../../hooks/use-services-step";

export interface Worker {
  id: string;
  displayName: string;
  user?: {
    firstName: string;
    lastName: string;
  } | null;
  color: string;
  isActive: boolean;
}

interface ServiceWorkerAssignmentProps {
  selectedServices: SelectedService[];
  workers: Worker[];
  onServicesChange: (services: SelectedService[]) => void;
  loading?: boolean;
}

export function ServiceWorkerAssignment({
  selectedServices,
  workers,
  onServicesChange,
  loading = false,
}: ServiceWorkerAssignmentProps) {
  const { t } = useTranslation("appointments");
  // Validate that assigned workers exist in available workers
  const invalidAssignments = selectedServices.filter(
    (service) => service.workerId && !workers.find((w) => w.id === service.workerId)
  );

  if (invalidAssignments.length > 0) {
    console.warn(
      "INVALID WORKER ASSIGNMENTS DETECTED:",
      invalidAssignments.map((s) => ({
        serviceName: s.name,
        invalidWorkerId: s.workerId,
        availableWorkerIds: workers.map((w) => w.id),
      }))
    );
  }
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [primaryWorkerId, setPrimaryWorkerId] = useState<string>("");

  // Get unique workers from selected services
  const assignedWorkers = [
    ...new Set(selectedServices.map((service) => service.workerId).filter(Boolean)),
  ]
    .map((workerId) => workers.find((w) => w.id === workerId))
    .filter(Boolean);

  // Handle simple mode - assign same worker to all services
  const handleSimpleWorkerChange = (workerId: string) => {
    setPrimaryWorkerId(workerId);
    const updatedServices = selectedServices.map((service) => ({
      ...service,
      workerId: workerId,
    }));
    onServicesChange(updatedServices);
  };

  // Handle advanced mode - assign worker to specific service
  const handleServiceWorkerChange = (serviceId: string, workerId: string) => {
    const updatedServices = selectedServices.map((service) =>
      service.id === serviceId ? { ...service, workerId } : service
    );
    onServicesChange(updatedServices);
  };

  // Switch to advanced mode
  const switchToAdvancedMode = () => {
    setIsAdvancedMode(true);
    // Reset all worker assignments when switching modes
    const updatedServices = selectedServices.map((service) => ({
      ...service,
      workerId: undefined,
    }));
    onServicesChange(updatedServices);
  };

  // Switch to simple mode
  const switchToSimpleMode = () => {
    setIsAdvancedMode(false);
    // Reset all worker assignments and primary worker when switching modes
    setPrimaryWorkerId("");
    const updatedServices = selectedServices.map((service) => ({
      ...service,
      workerId: undefined,
    }));
    onServicesChange(updatedServices);
  };

  // Check for potential conflicts or suggestions
  const hasConflicts = isAdvancedMode && assignedWorkers.length > 1;

  if (selectedServices.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">{t("createStep.workerAssignment")}</Label>
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="advanced-mode" className="text-xs text-muted-foreground">
            {t("createStep.advanced")}
          </Label>
          <Switch
            id="advanced-mode"
            checked={isAdvancedMode}
            onCheckedChange={(checked) => (checked ? switchToAdvancedMode() : switchToSimpleMode())}
            disabled={loading}
            data-test-id="appointment-create-worker-assignment-mode"
          />
        </div>
      </div>

      {!isAdvancedMode ? (
        /* Simple Mode */
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span>{t("createStep.assignOneWorkerAllServices")}</span>
          </div>
          <Select
            value={primaryWorkerId}
            onValueChange={handleSimpleWorkerChange}
            disabled={loading}
          >
            <SelectTrigger className="bg-background">
              <SelectValue placeholder={t("createStep.selectWorkerForAll")}>
                {primaryWorkerId &&
                  (() => {
                    const selectedWorker = workers.find((w) => w.id === primaryWorkerId);
                    return selectedWorker ? (
                      <div className="flex items-center gap-2">
                        {SHOW_WORKER_COLOR_DOT && (
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: selectedWorker.color }}
                          />
                        )}
                        {selectedWorker.displayName ||
                          (selectedWorker.user
                            ? `${selectedWorker.user.firstName} ${selectedWorker.user.lastName}`
                            : "Unknown Worker")}
                      </div>
                    ) : null;
                  })()}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {workers.map((worker) => (
                <SelectItem key={worker.id} value={worker.id}>
                  <div className="flex items-center gap-2">
                    {SHOW_WORKER_COLOR_DOT && (
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: worker.color }}
                      />
                    )}
                    {worker.displayName ||
                      (worker.user
                        ? `${worker.user.firstName} ${worker.user.lastName}`
                        : "Unknown Worker")}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {primaryWorkerId && (
            <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
              <span>{t("createStep.assignedTo")}:</span>
              {selectedServices.map((service, index) => (
                <Badge key={service.id} variant="secondary" className="text-xs">
                  {service.name}
                  {index < selectedServices.length - 1 && ","}
                </Badge>
              ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={switchToAdvancedMode}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            <Settings className="h-3 w-3 mr-1" />
            {t("createStep.customizePerService")}
          </Button>
        </div>
      ) : (
        /* Advanced Mode */
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Settings className="h-4 w-4" />
            <span>{t("createStep.assignWorkersPerService")}</span>
          </div>
          {hasConflicts && (
            <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
              <AlertTriangle className="h-3 w-3" />
              <span>{t("createStep.multipleWorkersWarning")}</span>
            </div>
          )}
          <div className="space-y-3">
            {selectedServices.map((service) => (
              <div
                key={service.id}
                className="flex items-center justify-between p-3 border rounded bg-card"
              >
                <div className="flex-1">
                  <div className="font-medium text-sm">{service.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {service.duration}min • <PriceDisplay amount={service.price} />
                  </div>
                </div>

                <div className="w-48">
                  <Select
                    value={service.workerId || ""}
                    onValueChange={(workerId) => handleServiceWorkerChange(service.id, workerId)}
                    disabled={loading}
                    data-test-id={`appointment-create-service-worker-${service.id}`}
                  >
                    <SelectTrigger className="h-8 text-xs bg-background">
                      <SelectValue placeholder={t("createStep.selectWorker")}>
                        {service.workerId &&
                          (() => {
                            const selectedWorker = workers.find((w) => w.id === service.workerId);
                            return selectedWorker ? (
                              <div className="flex items-center gap-2">
                                {SHOW_WORKER_COLOR_DOT && (
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: selectedWorker.color }}
                                  />
                                )}
                                <span className="text-xs">
                                  {selectedWorker.displayName ||
                                    (selectedWorker.user
                                      ? `${selectedWorker.user.firstName} ${selectedWorker.user.lastName}`
                                      : "Unknown Worker")}
                                </span>
                              </div>
                            ) : null;
                          })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {workers.map((worker) => (
                        <SelectItem key={worker.id} value={worker.id}>
                          <div className="flex items-center gap-2">
                            {SHOW_WORKER_COLOR_DOT && (
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: worker.color }}
                              />
                            )}
                            <span className="text-xs">
                              {worker.displayName ||
                                (worker.user
                                  ? `${worker.user.firstName} ${worker.user.lastName}`
                                  : "Unknown Worker")}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={switchToSimpleMode}
            className="text-xs text-muted-foreground hover:text-muted-foreground"
          >
            {t("createStep.switchToSimpleMode")}
          </Button>
        </div>
      )}

      {/* Summary */}
      {assignedWorkers.length > 0 && (
        <div className="pt-2 border-t">
          <div className="text-xs text-muted-foreground mb-1">
            {t("createStep.assignedWorkers")}:
          </div>
          <div className="flex flex-wrap gap-1">
            {assignedWorkers.map((worker) => (
              <Badge key={worker?.id} variant="outline" className="text-xs">
                {SHOW_WORKER_COLOR_DOT && (
                  <div
                    className="w-2 h-2 rounded-full mr-1"
                    style={{ backgroundColor: worker?.color }}
                  />
                )}
                {worker?.displayName ||
                  (worker?.user
                    ? `${worker?.user.firstName} ${worker?.user.lastName}`
                    : "Unknown Worker")}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
